/**
 * React NodeView for `videoEmbed` on the card-description + card/wiki
 * comment editor surfaces. Bound by `buildVideoEmbedExtension` in
 * `extensions.ts` when `surface === 'card' | 'comment'`.
 *
 * Same node and the same attr contract as the wiki embed — the BE
 * validation tier and its walkers key on `videoEmbed.attrs.fileId` /
 * `.caption`, so a second spelling would repeat the `fileEmbed`
 * `attachmentId` split. Only the presentation is card-flavoured:
 * `uploads`-namespace copy, `card-embed:*` upload events (the wiki
 * orchestrator is not mounted here and must not be asked to inspect
 * every card click), and column-width sizing instead of the wiki's
 * 720px bordered panel, which reads as a card nested inside the card
 * modal's own card.
 *
 * Branches, in priority order:
 *   0. FAILED — a placeholder whose upload errored. Checked before
 *      UPLOADING: a failed node still carries its placeholder id, which
 *      is how the owning composer re-targets it.
 *   1. UPLOADING — spinner, filename, percent, progress bar, Cancel.
 *   2. UNAVAILABLE — no file id and nothing uploading (a stripped or
 *      aborted leftover; cards have no slash menu, so there is no
 *      "pick a video" state), or the signed GET was refused. 403 and
 *      404 are deliberately indistinguishable, matching the rest of the
 *      card surface's existence minimization.
 *   3. LOADING — a presign round trip, ordered BEHIND `unplayable` so a
 *      refresh still in flight cannot flash a skeleton over a decision
 *      already made.
 *   4. READY — the shared `<AttachmentVideoPlayer>` plus the caption row.
 *
 * Geometry: a 640px-capped wrapper, and the three pre-ready branches
 * render the IDENTICAL 16:9 box so the paragraph under the embed does
 * not move when an upload resolves, fails, or is retried. 640 is the
 * widest 16:9 frame whose height (360) stays under the card image's
 * `max-h-96` cap, so a video and a full-size image read as siblings —
 * and it makes the common 16:9 case reflow-free, because the
 * post-metadata natural size is the same 640×360 the placeholder box
 * was.
 *
 * Sizing after metadata is `w-auto`, NOT `w-full`: a 9:16 phone
 * recording forced to 640 wide renders about 200px of video between
 * two 220px bars of black. Natural sizing costs a reflow only when the
 * source is not 16:9, which is exactly the case a fixed frame got wrong
 * anyway.
 *
 * Inside a table cell the player simply follows its column and degrades
 * the way Chrome degrades it — a complete native control bar needs
 * ~310px, and every comment surface (310 reply / 332 compose / 358
 * read) clears that while a 3-column cell does not. There is
 * deliberately no `min-w-*`: under `table-layout: fixed` a too-wide
 * child overflows its column instead of widening it, so the guard would
 * trade a small player for a broken table.
 */

import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { AlertCircle, FileVideo, Loader2, RotateCcw, X } from 'lucide-react'
import { type MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentVideoPlayer } from '@/components/AttachmentVideoPlayer'
import {
  type CardEmbedUploadAction,
  dispatchCardEmbedUploadAction,
} from '@/editor/nodes/card-embed-upload-events'
import { UploadProgressBar, uploadPercent } from '@/features/uploads/UploadProgressBar'
import { useFilePresignedUrl } from '@/features/wiki/hooks/use-file-presigned-url'
import { useUploadProgress } from '@/features/wiki/hooks/use-upload-guards'
import { formatPercent } from '@/i18n/formatters'

/**
 * `max-w-[40rem]` lives on the WRAPPER, not the video, so the caption
 * row can never run wider than the frame it labels. The container query
 * is NAMED so a future ancestor `@container` (the wiki doc header
 * already declares one) cannot silently capture the two panel branches'
 * width queries.
 */
const WRAPPER_CLASS = '@container/cardvideo my-2 w-full max-w-[40rem]'

/**
 * The box UPLOADING, FAILED, LOADING and the unplayable card all share.
 * Byte-identical geometry across them is what delivers the no-reflow
 * guarantee; only the border colour differs on the failed branch.
 */
const PANEL_CLASS =
  'relative flex aspect-video w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-md border bg-bg-muted px-4 text-center'

export function CardVideoEmbedView({
  node,
  updateAttributes,
  editor,
}: ReactNodeViewProps<HTMLElement>) {
  const { t, i18n } = useTranslation('uploads')
  const fileId = (node.attrs.fileId as string | null) ?? null
  const caption = (node.attrs.caption as string) ?? ''
  const uploadingPlaceholderId = (node.attrs.uploadingPlaceholderId as string | null) ?? null
  const uploadingFileName = (node.attrs.uploadingFileName as string | null) ?? null
  const uploadingFailed = Boolean(node.attrs.uploadingFailed)

  const uploadProgress = useUploadProgress(uploadingPlaceholderId)
  const uploadPercentValue = uploadPercent(uploadProgress)
  const { url, isLoading, isError, status, refresh } = useFilePresignedUrl(fileId)

  // Both of these track a FILE ID rather than a boolean, so they reset
  // for free when the upload flow writes a new `fileId` onto this same
  // node instance — no during-render setState, and no second copy of the
  // primitive's own reset.
  const [unplayableFileId, setUnplayableFileId] = useState<string | null>(null)
  const [sizedFileId, setSizedFileId] = useState<string | null>(null)
  const unplayable = fileId !== null && unplayableFileId === fileId
  const sized = fileId !== null && sizedFileId === fileId

  const [editingCaption, setEditingCaption] = useState(false)
  const [captionDraft, setCaptionDraft] = useState(caption)

  const editable = editor?.isEditable ?? false

  // Plain inline function, not a hook, so the hook count stays stable
  // across the uploading ↔ failed ↔ ready flips.
  const dispatchAction = (action: CardEmbedUploadAction) => (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!uploadingPlaceholderId) return
    dispatchCardEmbedUploadAction(action, uploadingPlaceholderId)
  }

  const commitCaption = () => {
    updateAttributes({ caption: captionDraft })
    setEditingCaption(false)
  }

  // --- 0. FAILED.
  if (uploadingPlaceholderId && uploadingFailed) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-video-embed"
        data-state="error"
        className={WRAPPER_CLASS}
        contentEditable={false}
      >
        <div className={`${PANEL_CLASS} border-danger/40`}>
          {/* The glyph keeps `text-danger` — an icon is a non-text
              graphic and clears the 3:1 floor — while the LABEL uses
              `text-danger-ink`, because `text-danger` on `bg-bg-muted`
              measures 4.41:1 on the Default theme and fails AA. */}
          <AlertCircle aria-hidden="true" size={24} className="text-danger" />
          {uploadingFileName ? (
            // The filename sits ABOVE the verdict: it is what identifies
            // WHICH of several dropped files failed. It is also the only
            // thing that sheds in a narrow container — Retry and Dismiss
            // are the sole way out of this state and never hide.
            <span
              className="hidden max-w-full truncate text-sm text-fg @[15rem]/cardvideo:block"
              title={uploadingFileName}
            >
              {uploadingFileName}
            </span>
          ) : null}
          <span className="text-sm font-medium text-danger-ink">{t('embed.failedLabel')}</span>
          <span className="mt-1 flex items-center gap-2">
            <button
              type="button"
              onClick={dispatchAction('retry')}
              // ProseMirror's selection-on-mousedown for this atomic node
              // otherwise swallows the click intent.
              onMouseDown={(e) => e.stopPropagation()}
              aria-label={t('embed.retryAria', { fileName: uploadingFileName ?? '' })}
              className="inline-flex min-h-6 items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs font-medium text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <RotateCcw aria-hidden="true" size={12} />
              {t('embed.retry')}
            </button>
            <button
              type="button"
              onClick={dispatchAction('dismiss')}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label={t('embed.dismissAria', { fileName: uploadingFileName ?? '' })}
              className="inline-flex min-h-6 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t('embed.dismiss')}
            </button>
          </span>
        </div>
      </NodeViewWrapper>
    )
  }

  // --- 1. UPLOADING.
  if (uploadingPlaceholderId) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-video-embed"
        data-state="uploading"
        className={WRAPPER_CLASS}
        contentEditable={false}
      >
        {/* `aria-busy` + plain spans, NOT `<output role="status">`: the
            owning composer already owns the sr-only announcement for an
            upload and the toast is a third. Same call the card file chip
            documents. */}
        <div aria-busy="true" className={`${PANEL_CLASS} border-border`}>
          <Loader2
            aria-hidden="true"
            size={24}
            className="animate-spin motion-reduce:animate-none"
          />
          {uploadingFileName ? (
            <span
              className="hidden max-w-full truncate text-sm text-fg @[15rem]/cardvideo:block"
              title={uploadingFileName}
            >
              {uploadingFileName}
            </span>
          ) : null}
          <span className="text-sm text-fg-muted">
            {t('embed.uploadingVideo')}
            {/* Absent until the first byte event — a zero reads as a
                stall, and the spinner above already says "started". */}
            {uploadPercentValue !== null ? (
              <span className="ms-1 font-medium tabular-nums text-fg">
                {formatPercent(uploadPercentValue / 100, i18n.language)}
              </span>
            ) : null}
          </span>
          {/* Bounded rather than full-bleed: at 640px a full-width 2px
              rule reads as a divider, not as a meter. `trackBg` is
              required — the panel is painted `bg-bg-muted`, which is
              also the bar's default track, so the track would vanish
              into its own panel and the fill would read as a stray
              rule. */}
          <span className="hidden w-full max-w-[20rem] @[11rem]/cardvideo:block">
            <UploadProgressBar
              progress={uploadProgress}
              label={
                uploadingFileName
                  ? t('progress.ariaLabel', { filename: uploadingFileName })
                  : t('progress.ariaLabelGeneric')
              }
              trackBg="bg-border"
            />
          </span>
          <button
            type="button"
            onClick={dispatchAction('cancel')}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={t('embed.cancelAria', { fileName: uploadingFileName ?? '' })}
            title={t('embed.cancel')}
            className="absolute end-2 top-2 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-elevated text-fg hover:bg-danger hover:text-danger-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X aria-hidden="true" size={14} />
          </button>
        </div>
      </NodeViewWrapper>
    )
  }

  // --- 2. UNAVAILABLE. Compact rather than a 16:9 panel: it is
  // terminal, never part of the upload chain, and a permanent 360px
  // empty box for a missing file would waste the column forever. The one
  // transition it takes part in (LOADING → UNAVAILABLE, when the signed
  // GET is refused) shrinks once, before the reader has begun reading.
  if (!fileId || isError || status === 403 || status === 404) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-video-embed"
        data-state="unavailable"
        className={WRAPPER_CLASS}
        contentEditable={false}
      >
        {/* `text-fg-muted`, NOT `text-fg-subtle`: subtle on `bg-bg-muted`
            measures 4.34:1 (Default) and 4.40:1 (Midnight), both under
            the AA floor. The card image view still carries that defect —
            don't copy it here. No `aria-label` on the outer span; the
            text inside carries the meaning. */}
        <span className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-muted px-3 py-2 text-sm text-fg-muted">
          <FileVideo size={16} aria-hidden="true" />
          <span>{t('embed.videoUnavailable')}</span>
        </span>
      </NodeViewWrapper>
    )
  }

  // --- 3. LOADING. The same box as UPLOADING / FAILED, so the common
  // stored-16:9 path to READY never moves the page. Visually silent —
  // a sub-second presign on a body that may hold several videos, and
  // three stacked spinners read as broken. The label is `sr-only` so
  // assistive tech still gets it.
  if (!unplayable && (isLoading || !url)) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-video-embed"
        data-state="loading"
        data-attachment-id={fileId}
        className={WRAPPER_CLASS}
        contentEditable={false}
      >
        <div
          aria-busy="true"
          className={`${PANEL_CLASS} animate-pulse border-border motion-reduce:animate-none`}
        >
          <span className="sr-only">{t('embed.videoLoading')}</span>
        </div>
      </NodeViewWrapper>
    )
  }

  // --- 4. READY.
  return (
    <NodeViewWrapper
      as="div"
      data-block-type="card-video-embed"
      data-state={unplayable ? 'unplayable' : 'ready'}
      data-attachment-id={fileId}
      className={WRAPPER_CLASS}
      contentEditable={false}
    >
      <AttachmentVideoPlayer
        fileId={fileId}
        url={url}
        refresh={refresh}
        onUnplayable={() => setUnplayableFileId(fileId)}
        onLoadedMetadata={() => setSizedFileId(fileId)}
        className={`${PANEL_CLASS} border-border`}
        // `bg-black` is the deliberate exception to the token system —
        // letterbox bars are CONTENT chrome, and every peer player uses
        // black. Which is exactly why the edge needs its own token:
        // `border-media-edge`, NOT `border-border`. On Obsidian and
        // Midnight the black body and the page ground are both very dark,
        // so a clip whose first frame is dark has no boundary at all —
        // `border-border` measured 1.16:1 there and `border-border-strong`
        // tops out at 1.64, so neither border token can do this job. The
        // media-edge token is tuned to clear the 3:1 floor a lone edge has
        // to meet (measured 4.48 / 4.49 / 3.85 across the three themes).
        //
        // Sizing note: a PORTRAIT source lands ~217px wide once `max-h-96`
        // binds, which is below the ~310px at which Chrome's native control
        // bar still shows its time readout — so a phone clip loses the
        // `0:00 / 0:05` even in a full-width description. That is the
        // browser degrading its own controls, and the alternative (forcing
        // the player wider) would pillarbox the very sources this avoids.
        videoClassName="block h-auto max-h-96 w-auto max-w-full rounded-md border border-media-edge bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        // Before metadata the element is held at the panel's exact 16:9
        // box, so there is no flash of the browser's 300×150 default and
        // no reflow when a 16:9 source resolves.
        videoStyle={
          sized
            ? { objectFit: 'contain' }
            : { objectFit: 'contain', width: '100%', aspectRatio: '16 / 9' }
        }
        labels={{
          ariaLabel: t('embed.videoAriaLabel'),
          fallback: t('embed.videoFallback'),
          unplayableTitle: t('embed.videoUnplayable'),
          unplayableBody: t('embed.videoUnplayableBody'),
          download: t('embed.videoDownload'),
          downloadFailed: t('embed.videoDownloadFailed'),
        }}
      />
      {/* Caption: three states, not two. A read-only non-empty caption
          renders as a plain `<p>` rather than a disabled button — a
          button that cannot be pressed is a dead affordance and adds a
          tab stop to every video in every read-only description. Empty
          and read-only renders nothing at all, which also keeps it from
          colliding with `EditableField`'s click-to-edit. */}
      {editingCaption ? (
        <input
          type="text"
          value={captionDraft}
          onChange={(e) => setCaptionDraft(e.target.value)}
          onBlur={commitCaption}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitCaption()
            } else if (e.key === 'Escape') {
              // Both stops are load-bearing: without them the card
              // `<Modal>`'s dialog-level handler reads the key and closes
              // the whole card instead of cancelling the field.
              e.preventDefault()
              e.stopPropagation()
              setCaptionDraft(caption)
              setEditingCaption(false)
            }
          }}
          placeholder={t('embed.captionPlaceholder')}
          aria-label={t('embed.captionAriaLabel')}
          // biome-ignore lint/a11y/noAutofocus: the edit flip is a user gesture — same pattern as EditableField.
          autoFocus
          className="mt-1 w-full rounded border border-border bg-bg-elevated px-2 py-0.5 text-center text-xs italic text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
      ) : caption ? (
        editable ? (
          <button
            type="button"
            onClick={() => {
              setCaptionDraft(caption)
              setEditingCaption(true)
            }}
            onMouseDown={(e) => e.stopPropagation()}
            title={t('embed.captionEditTitle')}
            className="mt-1 block w-full truncate rounded px-2 py-0.5 text-center text-xs italic text-fg-muted hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {caption}
          </button>
        ) : (
          <p className="mt-1 truncate px-2 text-center text-xs italic text-fg-muted">{caption}</p>
        )
      ) : editable ? (
        <button
          type="button"
          onClick={() => {
            setCaptionDraft(caption)
            setEditingCaption(true)
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="mt-1 block w-full rounded px-2 py-0.5 text-center text-xs italic text-fg-subtle hover:bg-bg-muted hover:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {t('embed.captionPlaceholder')}
        </button>
      ) : null}
    </NodeViewWrapper>
  )
}

CardVideoEmbedView.displayName = 'CardVideoEmbedView'
